/**
 * Print Health / Body chapter — Canva page-10.
 * Editorial header, hero-body, 4 horizontal week columns, Care/Rest/Move 3-up.
 * Live somatic data only; empty-state fallbacks when weeks or protocol are missing.
 */

import React from "react";
import type { LucideIcon } from "lucide-react";
import { HandHeart, Heart, MoonStar, PersonStanding } from "lucide-react";
import type { MonthlyConsultationBundle } from "../../../../utils/forecast/monthlyConsultation";
import heroBody from "../../../../assets/monthly-consultation/pdf/hero-body.jpg";
import { PdfChapterShell } from "../atoms";
import {
  clipPdfText,
  firstClauseHeading,
  protocolBuckets,
} from "../pdfCopy";
import {
  bodyFontFamily,
  border,
  cardWhite,
  charcoal,
  cream,
  displayFontFamily,
  mutedGray,
  paleTan,
  tanGold,
  terracotta,
} from "../pdfTheme";

/**
 * Props for the PDF health chapter.
 */
export interface PdfChapterHealthProps {
  /** Full monthly consultation payload. */
  bundle: MonthlyConsultationBundle;
  /** 1-based sequential page index among rendered sheets. */
  pageNumber?: number;
}

/** Soft cap for the intro pressure paragraph. */
const INTRO_CLIP = 180;
/** Soft cap for a week-signal description. */
const WEEK_CLIP = 96;
/** Soft cap for a Care / Rest / Move body line. */
const PROTOCOL_CLIP = 90;
/** Empty-state intro when element pressure is missing. */
const INTRO_FALLBACK =
  "This month's body cues follow the palace and season already named in this report.";
/** Empty-state week copy when a slot has no live signal. */
const WEEK_EMPTY = "No body signal was recorded for this week.";

/**
 * One padded week column for the horizontal body-signal row.
 */
interface BodyWeekColumn {
  /** Display label such as `Week 1`. */
  readonly week: string;
  /** Short serif heading. */
  readonly title: string;
  /** Supporting sentence. */
  readonly body: string;
}

/**
 * Care / Rest / Move print card.
 */
interface ProtocolCard {
  /** Slot number 01–03. */
  readonly marker: string;
  /** CARE / REST / MOVE. */
  readonly title: string;
  /** Instruction sentence. */
  readonly body: string;
  /** Lucide icon. */
  readonly Icon: LucideIcon;
}

/**
 * Resolve the webpack URL for the Body landscape hero.
 *
 * @returns Image URL, or empty string when the module did not yield a path
 */
function bodyHeroSrc(): string {
  return typeof heroBody === "string" ? heroBody.trim() : "";
}

/**
 * Pad a 1-based index to a two-digit print marker (`01`).
 *
 * @param index - 1-based index
 * @returns Zero-padded marker
 */
function paddedMarker(index: number): string {
  const n =
    typeof index === "number" && Number.isFinite(index) && index > 0
      ? Math.trunc(index)
      : 1;
  return String(n).padStart(2, "0");
}

/**
 * Thin gold rule with a diamond at the right end.
 *
 * @param props - Stroke color
 * @returns Rule row
 */
const PdfDiamondRule: React.FC<{ readonly color: string }> = ({ color }) => {
  const stroke =
    typeof color === "string" && color.trim().length > 0
      ? color.trim()
      : tanGold;
  return (
    <div
      aria-hidden="true"
      style={{
        display: "flex",
        alignItems: "center",
        width: "100%",
      }}
    >
      <span
        style={{
          flex: 1,
          height: 1,
          background: stroke,
        }}
      />
      <span
        style={{
          width: 6,
          height: 6,
          background: stroke,
          transform: "rotate(45deg)",
          flexShrink: 0,
          marginLeft: 1,
        }}
      />
    </div>
  );
};

/**
 * Short centered gold rule with a diamond in the middle (week columns + protocol).
 *
 * @param props - Optional width cap for week vs card rules
 * @returns Centered rule + diamond
 */
const PdfCenteredDiamondRule: React.FC<{
  readonly maxWidth?: number;
  readonly margin?: string;
}> = ({ maxWidth = 88, margin = "8px auto" }) => (
  <div
    aria-hidden="true"
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "72%",
      maxWidth,
      margin,
    }}
  >
    <span
      style={{
        flex: 1,
        height: 1,
        background: tanGold,
      }}
    />
    <span
      style={{
        width: 5,
        height: 5,
        background: tanGold,
        transform: "rotate(45deg)",
        flexShrink: 0,
        margin: "0 4px",
      }}
    />
    <span
      style={{
        flex: 1,
        height: 1,
        background: tanGold,
      }}
    />
  </div>
);

/**
 * Canva week timeline rail: gold line with diamond end-caps behind the markers.
 *
 * @returns Absolute timeline decoration
 */
const PdfWeekTimelineRail: React.FC = () => (
  <div
    aria-hidden="true"
    style={{
      position: "absolute",
      left: "5%",
      right: "5%",
      top: 15,
      display: "flex",
      alignItems: "center",
      pointerEvents: "none",
    }}
  >
    <span
      style={{
        width: 6,
        height: 6,
        background: tanGold,
        transform: "rotate(45deg)",
        flexShrink: 0,
      }}
    />
    <span
      style={{
        flex: 1,
        height: 1,
        background: tanGold,
      }}
    />
    <span
      style={{
        width: 6,
        height: 6,
        background: tanGold,
        transform: "rotate(45deg)",
        flexShrink: 0,
      }}
    />
  </div>
);

/**
 * Gold uppercase section label used for Body week + protocol blocks.
 *
 * @param props - Section label text
 * @returns Label paragraph
 */
const PdfBodySectionLabel: React.FC<{ readonly children: string }> = ({
  children,
}) => (
  <p
    style={{
      margin: "0 0 28px",
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.16em",
      textTransform: "uppercase",
      color: tanGold,
      fontFamily: bodyFontFamily,
    }}
  >
    {children}
  </p>
);

/**
 * Build four week columns from live somatic signals, padding empty slots.
 *
 * @param weeks - Bundle weekSignals
 * @returns Exactly four columns
 */
function bodyWeekColumns(
  weeks: ReadonlyArray<{ week: string; signal: string }>
): BodyWeekColumn[] {
  const live = Array.isArray(weeks) ? weeks : [];
  const columns: BodyWeekColumn[] = [];
  for (let index = 0; index < 4; index += 1) {
    const row = index < live.length ? live[index] : undefined;
    const weekLabel =
      row !== undefined && typeof row.week === "string" && row.week.trim().length > 0
        ? row.week.trim()
        : `Week ${String(index + 1)}`;
    const signal =
      row !== undefined && typeof row.signal === "string"
        ? row.signal.trim()
        : "";
    if (signal.length === 0) {
      columns.push({
        week: weekLabel,
        title: "No signal",
        body: WEEK_EMPTY,
      });
      continue;
    }
    const title = firstClauseHeading(signal, 5);
    columns.push({
      week: weekLabel,
      title: title.length > 0 ? title : weekLabel,
      body: clipPdfText(signal, WEEK_CLIP),
    });
  }
  return columns;
}

/**
 * Map protocol lines onto Care / Rest / Move print cards.
 *
 * @param protocol - Bundle protocol sentences
 * @returns Three cards
 */
function protocolCards(protocol: readonly string[]): ProtocolCard[] {
  const buckets = protocolBuckets(protocol);
  return [
    {
      marker: "01",
      title: "CARE",
      body: clipPdfText(buckets.care, PROTOCOL_CLIP),
      Icon: HandHeart,
    },
    {
      marker: "02",
      title: "REST",
      body: clipPdfText(buckets.rest, PROTOCOL_CLIP),
      Icon: MoonStar,
    },
    {
      marker: "03",
      title: "MOVE",
      body: clipPdfText(buckets.move, PROTOCOL_CLIP),
      Icon: PersonStanding,
    },
  ];
}

/**
 * Body / Health — one A4 sheet of week signals and Care / Rest / Move.
 */
export const PdfChapterHealth: React.FC<PdfChapterHealthProps> = ({
  bundle,
  pageNumber,
}) => {
  const pressureRaw =
    typeof bundle.somatic.elementPressure === "string"
      ? bundle.somatic.elementPressure.trim()
      : "";
  const intro =
    pressureRaw.length > 0
      ? clipPdfText(pressureRaw, INTRO_CLIP)
      : INTRO_FALLBACK;
  const weeks = Array.isArray(bundle.somatic.weekSignals)
    ? bundle.somatic.weekSignals
    : [];
  const protocol = Array.isArray(bundle.somatic.protocol)
    ? bundle.somatic.protocol
    : [];
  const columns = bodyWeekColumns(weeks);
  const cards = protocolCards(protocol);
  const hero = bodyHeroSrc();

  return (
    <PdfChapterShell
      chapterId="health"
      eyebrow=""
      title=""
      hideHeader={true}
      pageNumber={pageNumber}
      compact
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          fontFamily: bodyFontFamily,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            marginBottom: 24,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: `${tanGold}22`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              marginTop: 2,
            }}
          >
            <Heart size={18} color={terracotta} strokeWidth={1.8} />
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2
              style={{
                margin: 0,
                fontFamily: displayFontFamily,
                fontSize: 22,
                fontWeight: 700,
                lineHeight: 1.2,
                letterSpacing: "-0.02em",
                color: charcoal,
              }}
            >
              How Your Body May Feel This Month
            </h2>
            <p
              style={{
                margin: "6px 0 0",
                fontSize: 12,
                lineHeight: 1.45,
                color: mutedGray,
              }}
            >
              {intro}
            </p>
          </div>
        </div>
        <div style={{ marginBottom: 32 }}>
          <PdfDiamondRule color={tanGold} />
        </div>

        {hero.length > 0 ? (
          <img
            src={hero}
            alt=""
            className="mc-pdf-hero-img mc-pdf-hero-img--body"
            style={{ marginBottom: 56 }}
          />
        ) : null}

        <PdfBodySectionLabel>Week-by-week body signals</PdfBodySectionLabel>

        <div style={{ marginBottom: 64, position: "relative" }}>
          <PdfWeekTimelineRail />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr 1fr",
              marginBottom: 14,
              position: "relative",
              zIndex: 1,
            }}
          >
            {columns.map((col, index) => (
              <div
                key={`marker-${col.week}`}
                style={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background: terracotta,
                    color: cardWhite,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    lineHeight: 1,
                    boxShadow: `0 0 0 3px ${cream}`,
                  }}
                >
                  {paddedMarker(index + 1)}
                </span>
              </div>
            ))}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr 1fr",
              gap: 10,
            }}
          >
            {columns.map((col) => (
              <div
                key={`col-${col.week}`}
                style={{
                  textAlign: "center",
                  minWidth: 0,
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: terracotta,
                  }}
                >
                  {col.week}
                </p>
                <p
                  style={{
                    margin: "6px 0 0",
                    fontFamily: displayFontFamily,
                    fontSize: 13,
                    fontWeight: 700,
                    lineHeight: 1.3,
                    color: charcoal,
                  }}
                >
                  {col.title}
                </p>
                <PdfCenteredDiamondRule maxWidth={64} margin="7px auto" />
                <p
                  style={{
                    margin: 0,
                    fontSize: 10,
                    lineHeight: 1.45,
                    color: mutedGray,
                  }}
                >
                  {col.body}
                </p>
              </div>
            ))}
          </div>
        </div>

        <PdfBodySectionLabel>What to do for your body</PdfBodySectionLabel>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 10,
          }}
        >
          {cards.map((card) => {
            const Icon = card.Icon;
            return (
              <div
                key={card.title}
                className="mc-pdf-card"
                style={{
                  background: paleTan,
                  border: `1px solid ${border}`,
                  borderRadius: 14,
                  padding: "14px 12px 16px",
                  boxSizing: "border-box",
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: "50%",
                      background: tanGold,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon size={16} color={cardWhite} strokeWidth={1.8} />
                  </span>
                  <p
                    style={{
                      margin: 0,
                      display: "flex",
                      alignItems: "baseline",
                      gap: 6,
                      minWidth: 0,
                      fontFamily: displayFontFamily,
                      fontSize: 15,
                      fontWeight: 700,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      color: terracotta,
                      lineHeight: 1.15,
                    }}
                  >
                    <span>{card.marker}</span>
                    <span>{card.title}</span>
                  </p>
                </div>
                <PdfCenteredDiamondRule maxWidth={96} margin="10px auto 10px" />
                <p
                  style={{
                    margin: 0,
                    fontSize: 11,
                    lineHeight: 1.45,
                    color: charcoal,
                    textAlign: "center",
                  }}
                >
                  {card.body}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </PdfChapterShell>
  );
};

export default PdfChapterHealth;
